import classNames from "classnames";
import { useState } from "react";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { CaretDown } from "components/icons/CaretDown";
import { useToast } from "hooks/useToast";
import {
  RecentDocument,
  useBulkUploadDocumentsMutation,
  useGetEntityDocumentTypesQuery,
  useGetEntityOverviewQuery,
  useGetRecentDocumentsQuery,
} from "v2/store/entityDocumentsApi";
import { useDocumentUploadFlow } from "v2/utils/useDocumentUploadFlow";
import { Dropzone } from "./Dropzone";
import { SupportingDocumentsPanel } from "./SupportingDocumentsPanel";

type SetupDocumentsBoxProps = {
  entityId: string;
};

const SUPPORTING_DOCUMENTS_VALUE = "supporting_documents";
const NO_SECTION_OPEN = "";

const EMPTY_ITEMS: never[] = [];
const EMPTY_UPLOADED: RecentDocument[] = [];
const UPLOADED_BY_YOU = "you" as const;

const readErrorMessage = (error: unknown): string =>
  (error as { data?: { message?: string } })?.data?.message ??
  "Something went wrong";

export const SetupDocumentsBox = ({ entityId }: SetupDocumentsBoxProps) => {
  const { alertToast } = useToast();

  const [openSection, setOpenSection] = useState<string>(NO_SECTION_OPEN);

  const { data: requiredItems = EMPTY_ITEMS } = useGetEntityDocumentTypesQuery(
    { entityId },
    { skip: !entityId }
  );

  const { data: overview } = useGetEntityOverviewQuery(
    { entityId },
    { skip: !entityId }
  );

  const { data: uploaded = EMPTY_UPLOADED } = useGetRecentDocumentsQuery(
    { entityId, by: UPLOADED_BY_YOU },
    { skip: !entityId }
  );

  const [bulkUpload] = useBulkUploadDocumentsMutation();

  const handleUploadFiles = async (files: File[]): Promise<string[]> => {
    try {
      const uploaded = await bulkUpload({ entityId, files }).unwrap();
      return uploaded.map((document) => document.uuid);
    } catch (error) {
      alertToast({ message: readErrorMessage(error) });
      throw error;
    }
  };

  const { status, matchedCount, matchedIds, visibleItems, handleFiles } =
    useDocumentUploadFlow({
      entityId,
      items: requiredItems,
      onUploadFiles: handleUploadFiles,
    });

  const unmatchedCount = overview?.unmatchedCount ?? 0;

  const sectionedUploads = uploaded.filter(
    (document) => document.section !== null
  );

  const hasItems = visibleItems.length > 0;
  const hasUploads = sectionedUploads.length > 0;
  const hasUnmatched = unmatchedCount > 0;
  const isOpen = openSection === SUPPORTING_DOCUMENTS_VALUE;

  const caretClassName = classNames(
    "t-flex t-text-neutral-40 t-transition t-duration-300 t-ease-in-out",
    isOpen ? "t-rotate-0" : "-t-rotate-90"
  );

  return (
    <div className="t-flex t-shrink-0 t-flex-col t-gap-3 t-overflow-hidden t-rounded-lg t-border t-border-dashed t-border-neutral-20 t-bg-surface-lighter-grey">
      <Dropzone
        variant="compact"
        status={status}
        matchedCount={matchedCount}
        hasPendingItems={hasItems}
        onFiles={handleFiles}
      />

      {(hasItems || hasUploads || hasUnmatched) && (
        <Accordion.Root
          type="single"
          collapsible
          value={openSection}
          onValueChange={setOpenSection}
        >
          <Accordion.RawItem
            value={SUPPORTING_DOCUMENTS_VALUE}
            className="t-border t-border-solid t-border-neutral-0 t-bg-surface t-px-3"
          >
            <Accordion.RawTrigger asChild>
              <button
                type="button"
                className="all:unset t-flex t-w-full t-select-none t-items-center t-gap-1.5 t-py-2.5"
              >
                <span className={caretClassName}>
                  <CaretDown width="12" height="12" />
                </span>

                <span className="t-text-caption t-font-medium t-text-text-30">
                  Supporting documents
                </span>
              </button>
            </Accordion.RawTrigger>

            <Accordion.RawContent className="t-overflow-hidden data-state-closed:t-animate-accordian-slide-up data-state-open:t-animate-accordian-slide-down">
              <SupportingDocumentsPanel
                entityId={entityId}
                items={visibleItems}
                matchedIds={matchedIds}
                uploaded={sectionedUploads}
                unmatchedCount={unmatchedCount}
              />
            </Accordion.RawContent>
          </Accordion.RawItem>
        </Accordion.Root>
      )}
    </div>
  );
};
